import { memo, useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import type { SiteContent } from '../../types/content';
import { logger } from '../../utils/logger';

const Header = () => {
	const location = useLocation();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [siteContent, setSiteContent] = useState<SiteContent | null>(null);

	useEffect(() => {
		const fetchSiteContent = async () => {
			try {
				const response = await api.getSite();
				setSiteContent(response.data as SiteContent);
			} catch (err) {
				logger.error('Error fetching site content:', err);
				// Fallback to default values
				setSiteContent({
					companyName: 'S NEWHAM Groundworks',
					companyEmail: 'info@snewhamgroundworks.co.uk',
					companyContactNumber: '07824 612 098',
				});
			}
		};

		fetchSiteContent();
	}, []);

	const navItems = [
		{ path: '/', label: 'Home' },
		{ path: '/services', label: 'Services' },
		{ path: '/gallery', label: 'Gallery' },
		{ path: '/testimonials', label: 'Testimonials' },
		{ path: '/contact', label: 'Contact' },
	];

	const toggleMenu = useCallback(() => {
		setIsMenuOpen((prev) => !prev);
	}, []);

	const closeMenu = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	const handleMenuKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Escape' && isMenuOpen) {
				closeMenu();
			}
		},
		[isMenuOpen, closeMenu]
	);

	// Close menu when clicking outside or navigating
	useEffect(() => {
		if (!isMenuOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			if (!target.closest('nav')) {
				closeMenu();
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [isMenuOpen, closeMenu]);

	// Close menu on route change
	useEffect(() => {
		closeMenu();
	}, [location.pathname, closeMenu]);

	const companyName = siteContent?.companyName || 'S NEWHAM Groundworks';
	const logoAlt = siteContent?.companyLogo
		? `${companyName} logo`
		: `${companyName} Home`;

	return (
		<header role="banner">
			<nav role="navigation" aria-label="Main navigation">
				<div className="header-container">
					<Link
						to="/"
						className="logo"
						aria-label={`${companyName} Home`}
					>
						{siteContent?.companyLogo ? (
							<img
								src={siteContent.companyLogo}
								alt={logoAlt}
								className="logo-image"
								width="151"
								height="60"
							/>
						) : (
							companyName
						)}
					</Link>
					<button
						className="menu-toggle"
						aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
						aria-expanded={isMenuOpen}
						aria-controls="main-navigation"
						onClick={toggleMenu}
						onKeyDown={handleMenuKeyDown}
					>
						<span aria-hidden="true"></span>
						<span aria-hidden="true"></span>
						<span aria-hidden="true"></span>
					</button>
					<ul
						id="main-navigation"
						className={`nav-list ${isMenuOpen ? 'open' : ''}`}
						role="menubar"
					>
						{navItems.map((item) => (
							<li key={item.path} role="none">
								<Link
									to={item.path}
									className={location.pathname === item.path ? 'active' : ''}
									onClick={closeMenu}
									role="menuitem"
									aria-current={location.pathname === item.path ? 'page' : undefined}
								>
									{item.label}
								</Link>
							</li>
						))}
					</ul>
				</div>
			</nav>
		</header>
	);
};

export default memo(Header);
